import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";

import { Form, Input, Select, Button, Row, Col, Typography, Space, DatePicker } from "antd";
import { toast } from "../../components/toasts/toastStore";
import { getPaciente, crearPaciente, editarPaciente, subirFotoPaciente } from "./pacientesApi";
import { PATH } from "../../redux/utils";
import PacienteFotoPicker from "./PacienteFotoPicker";

import "./PacientesPage.css";

const { Title, Text } = Typography;

export default function CrearPacientePage() {
  const navigate = useNavigate();
  const { idPaciente } = useParams();
  const isEditing = !!idPaciente;

  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [loadingPaciente, setLoadingPaciente] = useState(isEditing);
  const [fotoFile, setFotoFile] = useState(null);
  const [fotoActual, setFotoActual] = useState(null);

  useEffect(() => {
    if (!isEditing) return;
    getPaciente(idPaciente)
      .then((res) => {
        const p = res.data ?? {};
        form.setFieldsValue({
          nombre_completo: p.nombre_completo,
          fecha_nacimiento: p.fecha_nacimiento ? dayjs(p.fecha_nacimiento) : undefined,
          telefono: p.telefono,
          correo: p.correo,
          direccion: p.direccion,
        });
        if (p.path && p.filename) setFotoActual(`${PATH.replace(/\/$/, "")}/${p.path}/${p.filename}`);
      })
      .catch(() => toast.error("No se pudo cargar el paciente"))
      .finally(() => setLoadingPaciente(false));
  }, [idPaciente]);

  const handleSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const payload = {
      nombre_completo: values.nombre_completo?.trim(),
      fecha_nacimiento: values.fecha_nacimiento ? values.fecha_nacimiento.format("YYYY-MM-DD") : null,
      telefono: values.telefono?.trim(),
      correo: values.correo?.trim() || null,
      direccion: values.direccion?.trim() || null,
    };

    setSaving(true);
    try {
      if (isEditing) {
        await editarPaciente(idPaciente, payload);
        if (fotoFile) await subirFotoPaciente(idPaciente, fotoFile);
        toast.success("Paciente actualizado correctamente");
        navigate(`/app/pacientes/${idPaciente}`);
      } else {
        const { data } = await crearPaciente(payload);
        if (fotoFile) {
          try {
            await subirFotoPaciente(data?.id, fotoFile);
          } catch (err) {
            toast.warning(err?.response?.data?.detail || "El paciente se creo, pero no se pudo subir la foto");
          }
        }
        toast.success(`"${payload.nombre_completo}" se registro correctamente`);
        navigate(`/app/pacientes/${data?.id}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Error al guardar el paciente");
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="paq-main">
      <div className="paq-content">
        <section className="cc-page-header-card">
          <Space direction="vertical" size={2}>
            <Title level={2} className="paq-title" style={{ marginBottom: 0 }}>
              {isEditing ? "Editando paciente" : "Nuevo paciente"}
            </Title>
            <Text className="paq-subtitle">
              {isEditing
                ? "Modifica los datos generales del paciente y guarda los cambios."
                : "Completa los datos generales del paciente para registrarlo en el sistema."}
            </Text>
          </Space>

          <Space>
            <Button className="eventos-btn-clean" onClick={() => navigate(-1)} disabled={saving}>
              Cancelar
            </Button>
            <Button
              type="primary"
              loading={saving}
              icon={!isEditing ? <i className="fa-solid fa-plus" /> : null}
              onClick={handleSave}
              style={{ backgroundColor: "var(--brand-primary)", borderColor: "var(--brand-primary)" }}
            >
              {isEditing ? "Guardar cambios" : "Crear paciente"}
            </Button>
          </Space>
        </section>

        <Form form={form} layout="vertical" disabled={loadingPaciente}>
          <div className="cc-section-card">
            <div className="cc-section-header">
              <span className="cc-section-icon"><i className="fa-solid fa-user" /></span>
              Datos del paciente
            </div>

            <Row gutter={16}>
              <Col xs={24}>
                <div className="paq-field-label">Foto del paciente (opcional)</div>
                <PacienteFotoPicker value={fotoActual} onChange={setFotoFile} />
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="nombre_completo"
                  label={<span className="paq-field-label">Nombre completo</span>}
                  rules={[{ required: true, message: "Requerido" }]}
                >
                  <Input placeholder="Ej. Maria Fernanda Lopez Garcia" autoComplete="off" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="fecha_nacimiento"
                  label={<span className="paq-field-label">Fecha de nacimiento</span>}
                  rules={[{ required: true, message: "Requerido" }]}
                >
                  <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" placeholder="Selecciona la fecha" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="telefono"
                  label={<span className="paq-field-label">Telefono</span>}
                  rules={[{ required: true, message: "Requerido" }]}
                >
                  <Input placeholder="Ej. 6621234567" autoComplete="off" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="correo" label={<span className="paq-field-label">Correo (opcional)</span>}>
                  <Input placeholder="Ej. paciente@correo.com" autoComplete="off" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item name="direccion" label={<span className="paq-field-label">Direccion (opcional)</span>}>
                  <Input placeholder="Calle, numero, colonia, ciudad" autoComplete="off" />
                </Form.Item>
              </Col>
            </Row>
          </div>
        </Form>
      </div>
    </main>
  );
}
